"use client";

import { useMemo, memo } from "react";

import parse from "html-react-parser";

import { createHtmlTransform } from "./html-transform";
import { wrapInlineMathInHtml } from "./renderers";
import type { Element } from "./utils/type";

interface FormattedHtmlContentProps {
  element: Element;
  index: number;
}

const trimBr = (html: string): string => {
  // 앞쪽 연속 <br> 제거
  html = html.replace(/^(\s*<br\s*\/?>)+/gi, "");
  // 뒤쪽 연속 <br> 제거
  html = html.replace(/(\s*<br\s*\/?>)+\s*$/gi, "");
  return html;
};

/**
 * HTML 컨텐츠를 마크다운 스타일로 렌더링하는 컴포넌트
 * - html 문자열이 변경되지 않는 한 html-react-parser 파싱 결과를 메모이즈하여
 *   불필요한 재계산을 줄인다.
 */
export const FormattedHtmlContent = memo<FormattedHtmlContentProps>(
  function FormattedHtmlContent({ element, index }: FormattedHtmlContentProps) {
    const { html } = element.content || {};

    const parsed = useMemo(() => {
      if (!html) {
        return null;
      }

      // 파싱 전에 문단 텍스트에 섞인 인라인 수식($...$ 등)을 <imath> element로 감싼다.
      const preparedHtml = wrapInlineMathInHtml(trimBr(html));
      const options = {
        transform: createHtmlTransform(element, index),
      };

      return parse(preparedHtml, options);
    }, [html, element, index]);

    // html-react-parser가 string, ReactElement 등을 반환하므로 Fragment로 감싸서 ReactElement로 변환
    return <>{parsed}</>;
  },
);
